import React, {useState, useEffect} from 'react';
import Avatar from 'react-avatar';

import Card from '../../components/Card';
import Link from '../../components/Link';
import Pager from '../../components/Pager';
import {Tabs} from '../../components/Tabs';
import KarmaPill, {KarmaPillSizes} from '../../components/KarmaPill';
import {getKarmaLeaders, getUserKarmaRank} from '../../api/api';
import {useAppContext, useAnalytics} from '../../hooks';
import {formatLargeNum} from '../../util';

const Leaderboards = {
  MONTHLY: 'monthly',
  ALL: 'all',
};

const TabList = [
  {key: Leaderboards.MONTHLY, label: 'Last 30 days'},
  {key: Leaderboards.ALL, label: 'All-time'},
];

const LeaderboardItem = ({user, rank}) => {
  const {tracker} = useAnalytics();

  return (
    <div className="flex justify-between items-center mb-2">
      <div>
        <span className="font-medium text-base text-gray-400 mr-3">
          {`#${rank}`}
        </span>
        <Link
          href={`/users/${user.uuid}`}
          onClick={() => tracker.karmaLeaderboardClickUser({
            uuid: user.uuid,
            rank,
          })}
        >
          <Avatar
            className="bg-gray-100 size-10 mr-4 rounded-full shrink-0"
            name={user.displayName}
            src={user.profileImage || null}
            color="#f0f0f0"
            fgColor="#000000"
            size={32}
          />
          <span className="font-semibold">
            {user.displayName}
          </span>
        </Link>
      </div>
      <div>
        <KarmaPill
          score={user.karma}
          size={KarmaPillSizes.REGULAR}
        />
      </div>
    </div>
  );
};

const LeaderboardList = ({list, onPageChange}) => {
  if (!list) return null;

  const startingRank = (list.pagination.current - 1) * list.pagination.limit + 1;

  return (
    <>
      <ol className="mb-5">
        {list.results.map((user, i) => (
          <LeaderboardItem
            user={user}
            rank={startingRank + i}
            key={user.id}
          />
        ))}
      </ol>
      {list.pagination.count > list.pagination.limit && (
      <Pager
        pagination={list.pagination}
        showDirectionButtons={false}
        maxPage={10}
        onClick={onPageChange}
      />
      )}
    </>
  );
};


const Leaderboard = ({className}) => {
  const [activeTab, setActiveTab] = useState(Leaderboards.MONTHLY);
  const [leaders, setLeaders] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.karmaViewKarmaPage();
    getKarmaLeaders().then(setLeaders);
  }, []);

  useEffect(() => {
    if (currentUser) getUserKarmaRank().then(setUserRank);
  }, [currentUser]);

  const handlePageChange = (type) => async (pageDelta) => {
    const leaderPage = await getKarmaLeaders({
      type,
      page: leaders[type].pagination.current + pageDelta,
    });

    setLeaders((curr) => ({
      ...curr,
      [type]: leaderPage[type],
    }));
  };

  return (
    <div className={className}>
      <h2 className="text-lg font-bold mb-4">Leaderboard</h2>
      <Card>
        {userRank && (
          <p className="text-gray-400 font-medium">
            Your all-time rank is
            <span className="text-base text-gray-600 font-semibold">
              {` #${userRank.all.rank}`}
            </span>
            {userRank.monthly?.rank && (
            <span>
              . In the last 30 days, you&apos;ve earned
              <span className="text-base text-gray-600 font-semibold">
                {` ${formatLargeNum(userRank.monthly.karma)} karma `}
              </span>
              and you&apos;re ranked
              <span className="text-base text-gray-600 font-semibold">
                {` #${userRank.monthly.rank}`}
              </span>
              .
            </span>
            )}
          </p>
        )}

        <Tabs
          tabs={TabList}
          active={activeTab}
          onClick={setActiveTab}
          className="mb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 "
        />

        {activeTab === Leaderboards.MONTHLY && (
        <LeaderboardList
          list={leaders?.monthly}
          onPageChange={handlePageChange(Leaderboards.MONTHLY)}
        />
        )}

        {activeTab === Leaderboards.ALL && (
        <LeaderboardList
          list={leaders?.all}
          onPageChange={handlePageChange(Leaderboards.ALL)}
        />
        )}
      </Card>
    </div>
  );
};

export default Leaderboard;
